import { Box, IconButton, Link, Tooltip } from '@mui/material';
import AQtiveGuardLogo from '../../assets/images/aqtive-guard-logo.svg';
import SbtLogo from '../../assets/images/sbt-logo.svg';
import { ActionMenu } from '../../common/components/ActionMenu';
import { UrlHelper } from '../../common/utils/UrlHelper';
import { NAVBAR_DARK_THEME, NAVBAR_LIGHT_THEME } from '../../config/theme/navBarTheme';
import { PATHS, Route } from '../../routing/routes';
import { NavTabs } from './NavTabs';

const getIconButtonStyles = ({ isActive }: { isActive: boolean }) => ({
  '& svg': { color: isActive ? 'white' : 'grey.600' },
  '&:hover': { '& svg': { color: 'white' } },
  '&.Mui-focusVisible': { color: 'grey.400' },
});

export interface NavBarProps {
  routes: Route[];
  buttonRoutes: Route[];
}

export const NavBar = ({ routes, buttonRoutes }: NavBarProps) => {
  return (
    <Box
      id="cc-navbar"
      component="header"
      sx={{
        backgroundColor: 'navbarBackground.main',
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', flex: '1 1 auto', overflow: 'hidden' }}>
        <Link
          href={PATHS.ORGANIZATION_DASHBOARD}
          sx={{
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            px: 'var(--secondary-outlet-px)',
            flex: '0 0 auto',
            textDecoration: 'none',
            '&:hover': { textDecoration: 'none' },
            '& .aqtive-guard-logo-full': { display: { xs: 'none', md: 'initial' } },
            '& .aqtive-guard-logo-icon': { display: { xs: 'initial', md: 'none' } },
          }}
        >
          <SbtLogo className="aqtive-guard-logo-icon" role="img" title="AQtive Guard" height="32" width="32" viewBox="0 0 56 56" fill="#E5A724" />
          <AQtiveGuardLogo className="aqtive-guard-logo-full" role="img" title="AQtive Guard" />
        </Link>

        {routes.length > 0 && (
          <Box sx={{ flex: '1 1 auto', overflow: 'hidden' }}>
            <NavTabs theme={NAVBAR_DARK_THEME} tabs={routes} />
          </Box>
        )}
      </Box>

      {buttonRoutes.length > 0 && (
        <Box id="navbar-icon-buttons" sx={{ display: 'flex', alignItems: 'center', pl: 3, pr: 2, flex: '0 0 auto' }}>
          {buttonRoutes.map((buttonRoute, index) => {
            const buttonStyles = getIconButtonStyles({
              isActive: !![
                UrlHelper.isCurrentPath(buttonRoute.path),
                ...(buttonRoute?.children?.map((child) => UrlHelper.isCurrentPath(child.path)) || []),
              ].find((match) => match),
            });

            if (buttonRoute.children) {
              return (
                <ActionMenu
                  key={index}
                  label={buttonRoute.name}
                  buttonStyles={buttonStyles}
                  Icon={buttonRoute.Icon}
                  actions={buttonRoute.children.map((item) => ({
                    label: item.name || '',
                    link: item.path ? { href: item.path, target: item.openInNewTab ? '_blank' : '' } : undefined,
                    onClick: item.onClick,
                    isSelected: UrlHelper.isCurrentPath(item.path),
                    divider: item.divider,
                    'data-role': item['data-role'],
                  }))}
                  theme={NAVBAR_LIGHT_THEME}
                ></ActionMenu>
              );
            }

            return (
              <Tooltip key={index} title={buttonRoute.name} placement="bottom">
                <IconButton sx={buttonStyles} href={buttonRoute.path} target={buttonRoute.openInNewTab ? '_blank' : ''}>
                  {buttonRoute.Icon && <buttonRoute.Icon />}
                </IconButton>
              </Tooltip>
            );
          })}
        </Box>
      )}
    </Box>
  );
};
